// /api/contacts hooks — Session 7 PR3 outreach helper.

import { useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  Contact,
  CreateContactRequest,
  DraftMessageRequest,
  DraftMessageResponse,
  MarkMessageSentRequest,
  PatchContactRequest,
} from "@resume-cloudflare/shared";
import { api, ApiError } from "./api";
import { useMutationToast } from "../hooks/useMutationToast";

export interface ContactRow extends Contact {
  company_name: string | null;
  link_count: number;
}

export interface ContactsResponse {
  contacts: ContactRow[];
}

export function useContacts() {
  return useQuery({
    queryKey: ["contacts"],
    queryFn: () => api<ContactsResponse>("/api/contacts"),
    staleTime: 10_000,
  });
}

export function useContact(id: string | null) {
  return useQuery({
    queryKey: ["contact", id],
    enabled: id !== null,
    queryFn: () =>
      api<{
        contact: Contact;
        links: Array<{
          application_id: string;
          role_in_process: string | null;
          role: string;
          status: string;
        }>;
      }>(`/api/contacts/${id}`),
  });
}

export function useCreateContact() {
  const qc = useQueryClient();
  return useMutationToast<{ contact: Contact }, ApiError, CreateContactRequest>({
    mutationFn: (input) =>
      api<{ contact: Contact }>("/api/contacts", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["contacts"] }),
    success: "Contact saved.",
    errorPrefix: "Save failed",
  });
}

export function usePatchContact(id: string) {
  const qc = useQueryClient();
  return useMutationToast<{ contact: Contact }, ApiError, PatchContactRequest>({
    mutationFn: (input) =>
      api<{ contact: Contact }>(`/api/contacts/${id}`, {
        method: "PATCH",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contacts"] });
      qc.invalidateQueries({ queryKey: ["contact", id] });
    },
    success: "Contact updated.",
    errorPrefix: "Update failed",
  });
}

export function useDeleteContact() {
  const qc = useQueryClient();
  return useMutationToast<{ ok: true }, ApiError, { id: string }>({
    mutationFn: ({ id }) =>
      api<{ ok: true }>(`/api/contacts/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["contacts"] }),
    success: "Contact deleted.",
    errorPrefix: "Delete failed",
  });
}

// Draft a message — does NOT persist anything. The body is returned for
// the user to review and (separately) mark-sent.
export function useDraftMessage(contactId: string) {
  return useMutationToast<DraftMessageResponse, ApiError, DraftMessageRequest>({
    mutationFn: (input) =>
      api<DraftMessageResponse>(`/api/contacts/${contactId}/messages/draft`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    success: false,
    errorPrefix: "Draft failed",
  });
}

// Persist the (possibly user-edited) body as sent: inserts an artifact,
// embeds into APPLICATION_ARTIFACTS_INDEX, bumps last_contacted_at, and
// fires the application's agent timeline event when application_id is set.
export function useMarkMessageSent(contactId: string) {
  const qc = useQueryClient();
  return useMutationToast<
    { artifact_id: string; vector_id: string | null; last_contacted_at: number },
    ApiError,
    MarkMessageSentRequest
  >({
    mutationFn: (input) =>
      api<{ artifact_id: string; vector_id: string | null; last_contacted_at: number }>(
        `/api/contacts/${contactId}/messages/sent`,
        {
          method: "POST",
          body: JSON.stringify(input),
        },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["contacts"] });
      qc.invalidateQueries({ queryKey: ["contact", contactId] });
      if (vars.application_id) {
        qc.invalidateQueries({ queryKey: ["application-timeline", vars.application_id] });
      }
    },
    success: "Marked sent.",
    errorPrefix: "Mark sent failed",
  });
}
